import React from "react"
import Link from "next/link"
import styled, { css } from "styled-components"
import { Image, ResponsiveImageType } from "react-datocms"
import { bodyBold } from "@constants/typography"
import colors from "@constants/colors"

interface BannerStripProps {
  text?: string
  backgroundColor?: string
  backgroundImage?: ResponsiveImageType
  backgroundImageSrc?: string
  textColor?: string
  fontWeight?: string
  height?: number
  redirectLink?: string
}

const stripStyles = css<{
  backgroundColor?: string
  textColor?: string
  fontWeight?: string
  heightPx: number
}>`
  width: 100%;
  min-height: ${({ heightPx }) => `${heightPx}px`};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  text-align: center;
  text-decoration: none;
  background-color: ${({ backgroundColor }) => backgroundColor || colors.black};
  color: ${({ textColor }) => textColor || colors.snow};
  font-weight: ${({ fontWeight }) => fontWeight || "700"};
`

const Strip = styled.section<{
  backgroundColor?: string
  textColor?: string
  fontWeight?: string
  heightPx: number
}>`
  ${stripStyles}
`

const StripLink = styled.a<{
  backgroundColor?: string
  textColor?: string
  fontWeight?: string
  heightPx: number
}>`
  ${stripStyles}
  cursor: pointer;
`

const ImageStrip = styled.section<{
  heightPx: number
  backgroundColor?: string
}>`
  ${({ heightPx, backgroundColor }) => css`
    width: 100%;
    min-height: ${heightPx}px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: ${backgroundColor || colors.black};
  `}
`

const ImageStripLink = styled.a<{ heightPx: number; backgroundColor?: string }>`
  ${({ heightPx, backgroundColor }) => css`
    width: 100%;
    min-height: ${heightPx}px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: ${backgroundColor || colors.black};
    cursor: pointer;
  `}
`

const ImageContainer = styled.div`
  width: 100%;
  max-width: 1680px;
  margin: 0 auto;
`

const ResponsiveBannerImage = styled(Image)`
  width: 100%;
  height: 100%;
`

const BannerImage = styled.img.attrs({
  decoding: "async",
})`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`

const Text = styled.span`
  ${bodyBold}
  margin: 0;
  color: inherit;
  font-weight: inherit;
`

const BannerStrip: React.FC<BannerStripProps> = ({
  text,
  backgroundColor,
  backgroundImage,
  backgroundImageSrc,
  textColor,
  fontWeight,
  height = 48,
  redirectLink,
}) => {
  const heightPx = Number.isFinite(height) ? Math.max(height, 1) : 48
  const hasImage = Boolean(backgroundImage || backgroundImageSrc)

  if (!hasImage && !text) return null

  if (hasImage) {
    if (!redirectLink) {
      return (
        <ImageStrip heightPx={heightPx} backgroundColor={backgroundColor}>
          <ImageContainer>
            {backgroundImage ? (
              <ResponsiveBannerImage data={backgroundImage} />
            ) : (
              <BannerImage src={backgroundImageSrc} alt="" />
            )}
          </ImageContainer>
        </ImageStrip>
      )
    }

    const isExternal = /^https?:\/\//i.test(redirectLink)
    if (isExternal) {
      return (
        <ImageStripLink
          href={redirectLink}
          heightPx={heightPx}
          backgroundColor={backgroundColor}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ImageContainer>
            {backgroundImage ? (
              <ResponsiveBannerImage data={backgroundImage} />
            ) : (
              <BannerImage src={backgroundImageSrc} alt="" />
            )}
          </ImageContainer>
        </ImageStripLink>
      )
    }

    return (
      <Link href={redirectLink} passHref legacyBehavior prefetch={false}>
        <ImageStripLink heightPx={heightPx} backgroundColor={backgroundColor}>
          <ImageContainer>
            {backgroundImage ? (
              <ResponsiveBannerImage data={backgroundImage} />
            ) : (
              <BannerImage src={backgroundImageSrc} alt="" />
            )}
          </ImageContainer>
        </ImageStripLink>
      </Link>
    )
  }

  if (!redirectLink) {
    return (
      <Strip
        backgroundColor={backgroundColor}
        textColor={textColor}
        fontWeight={fontWeight}
        heightPx={heightPx}
      >
        <Text>{text}</Text>
      </Strip>
    )
  }

  const isExternal = /^https?:\/\//i.test(redirectLink)
  if (isExternal) {
    return (
      <StripLink
        href={redirectLink}
        backgroundColor={backgroundColor}
        textColor={textColor}
        fontWeight={fontWeight}
        heightPx={heightPx}
      >
        <Text>{text}</Text>
      </StripLink>
    )
  }

  return (
    <Link href={redirectLink} passHref legacyBehavior prefetch={false}>
      <StripLink
        backgroundColor={backgroundColor}
        textColor={textColor}
        fontWeight={fontWeight}
        heightPx={heightPx}
      >
        <Text>{text}</Text>
      </StripLink>
    </Link>
  )
}

export default BannerStrip
